/**
 * Sentry initialization for the React SPA.
 *
 * Called once from `main.tsx` before the app renders. Empty DSN (default in
 * dev) disables the integration entirely — no network calls, no overhead.
 *
 * PII policy mirrors the backend: we never forward patient data. The axios
 * client strips breadcrumb bodies for routes that carry Tiptap document
 * state or Feegow patient payloads.
 */
import * as Sentry from '@sentry/react'

const PII_BREADCRUMB_URL_PATTERNS = [
  '/api/proposals/',
  '/api/feegow/patient/',
  '/api/contracts/',
]

const ENV = import.meta.env.VITE_SENTRY_ENV || 'local'
const DSN = import.meta.env.VITE_SENTRY_DSN || ''
const TRACES_SAMPLE = Number(
  import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0.1',
)

export function initSentry(): void {
  if (!DSN) {
    // Empty DSN is the default for local dev. Keep initialization off.
    // eslint-disable-next-line no-console
    console.info('[Sentry] DSN empty — observability disabled.')
    return
  }

  Sentry.init({
    dsn: DSN,
    environment: ENV,
    // `sendDefaultPii` controls auto-attaching cookies, IP, and request body.
    // Always false — we forward only staff email via setUser().
    sendDefaultPii: false,
    // Errors: always.
    sampleRate: 1.0,
    // Traces: modest sampling to keep volume manageable.
    tracesSampleRate: Number.isFinite(TRACES_SAMPLE) ? TRACES_SAMPLE : 0.1,
    // Session replay stays off — PII risk is too high on a clinical ERP.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Logs http.client spans for every outbound fetch/xhr.
      Sentry.httpClientIntegration(),
    ],
    beforeBreadcrumb(breadcrumb) {
      // Strip breadcrumb bodies for routes that carry Tiptap document_state
      // or Feegow patient payloads. Keep url/method/status for correlation.
      const url = String(breadcrumb.data?.url ?? '')
      if (
        breadcrumb.category === 'xhr' ||
        breadcrumb.category === 'fetch' ||
        breadcrumb.category === 'http'
      ) {
        if (PII_BREADCRUMB_URL_PATTERNS.some((p) => url.includes(p))) {
          if (breadcrumb.data) {
            delete (breadcrumb.data as Record<string, unknown>).requestBody
            delete (breadcrumb.data as Record<string, unknown>).responseBody
          }
        }
      }
      // Console breadcrumbs can log toasts with patient names — drop args.
      if (breadcrumb.category === 'console' && breadcrumb.data) {
        ;(breadcrumb.data as Record<string, unknown>).arguments = '[Filtered]'
      }
      return breadcrumb
    },
    beforeSend(event) {
      // Drop request body if it slipped through.
      if (event.request?.data) {
        event.request.data = '[Filtered]'
      }
      // Redact authorization header last-chance.
      const headers = event.request?.headers
      if (headers && typeof headers === 'object') {
        for (const k of Object.keys(headers)) {
          if (k.toLowerCase() === 'authorization') {
            ;(headers as Record<string, string>)[k] = '[Filtered]'
          }
        }
      }
      return event
    },
  })

  // eslint-disable-next-line no-console
  console.info(`[Sentry] initialized (env=${ENV}, traces=${TRACES_SAMPLE}).`)
}

/** Attach staff user context; called after login, cleared on logout. */
export function setSentryUser(user: {
  id: number | null
  email: string | null
  name: string
  role: string
} | null): void {
  if (!user) {
    Sentry.setUser(null)
    return
  }
  Sentry.setUser({
    id: user.id ?? undefined,
    email: user.email ?? undefined,
    username: user.name,
  })
  Sentry.setTag('user_role', user.role)
}

/** Stash the server-issued X-Request-Id on the active span, for correlation. */
export function tagRequestId(requestId: string | null | undefined): void {
  if (!requestId) return
  Sentry.getCurrentScope().setTag('request_id', requestId)
}
